/**
 * Shared category picker config — used by the right-click "Set category"
 * submenu, the inline chip dropdown on email rows, and the inbox filter
 * bar so all three stay in sync.
 *
 * Order matches what construction-business users care about most: the
 * money flow first (quote -> contract -> invoice -> payment), then
 * operational signals, then misc.
 */

export interface CategoryPickerOption {
  /** API key sent to the backend. Must match a key in
   *  ``EMAIL_CATEGORIES`` (organisation/services/classification_service/config.py). */
  key: string
  /** Human-friendly label rendered in menus. */
  label: string
  /** Tailwind background colour for the picker dot. Pulls the same
   *  palette as ``CATEGORY_CONFIG`` in InboxPage so the visuals stay
   *  consistent across the chip / filter bar / picker. */
  dotColor: string
}

export const CATEGORY_PICKER_OPTIONS: CategoryPickerOption[] = [
  { key: 'bill',             label: 'Supplier Bill',  dotColor: '#f97316' /* orange-500 */ },
  { key: 'quote_request',    label: 'Quote Request',  dotColor: '#3b82f6' /* blue-500 */ },
  { key: 'quote_submission', label: 'Quote Sent',     dotColor: '#6366f1' /* indigo-500 */ },
  { key: 'contract',         label: 'Contract',       dotColor: '#8b5cf6' /* violet-500 */ },
  { key: 'invoice',          label: 'Our Invoice',    dotColor: '#22c55e' /* green-500 */ },
  { key: 'payment',          label: 'Payment',        dotColor: '#10b981' /* emerald-500 */ },
  { key: 'variation',        label: 'Variation',      dotColor: '#f59e0b' /* amber-500 */ },
  { key: 'supplier',         label: 'Supplier',       dotColor: '#06b6d4' /* cyan-500 */ },
  { key: 'delivery',         label: 'Delivery',       dotColor: '#0ea5e9' /* sky-500 */ },
  { key: 'site_update',      label: 'Site Update',    dotColor: '#a855f7' /* purple-500 */ },
  { key: 'quality',          label: 'Quality',        dotColor: '#14b8a6' /* teal-500 */ },
  { key: 'safety',           label: 'Safety',         dotColor: '#ef4444' /* red-500 */ },
  { key: 'timesheet',        label: 'Timesheet',      dotColor: '#ec4899' /* pink-500 */ },
  { key: 'general',          label: 'General',        dotColor: '#9ca3af' /* gray-400 */ },
  { key: 'marketing',        label: 'Marketing',      dotColor: '#d946ef' /* fuchsia-500 */ },
  { key: 'spam',             label: 'Promotion',      dotColor: '#fb7185' /* rose-400 */ },
  // Junk is a rule/user-only bucket — the AI classifier never assigns it
  // (it's not in the backend EMAIL_CATEGORIES taxonomy; the alias map
  // coerces a raw "junk" to "spam"). Picking it from the menu pins the
  // override; the "Junk & auto-trash sender" action also soft-bins +
  // (optionally) arms an auto-trash rule.
  { key: 'junk',             label: 'Junk',           dotColor: '#9f1239' /* rose-800 */ },
]
